import { FundResult } from '@kurtosys/types/portal/common/statistics/response/result.js';
import {
	IScenarioStatisticsResponse,
	TPortfolioEntityGetResponse,
	TPortfolioGetResponse,
} from '@kurtosys/types/portal/index.js';
import { IExecutionOptions } from '@kurtosys/types/query/IExecutionOptions.js';
import { IQueryPortfoliosScatterChartDataOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryPortfoliosScatterChartDataOptions.js';
import { ScenarioStatisticShortcut } from '@kurtosys/types/query/scenarioStatistics/ScenarioStatisticShortcut.js';
import { IQueryShortcutOptions, ShortcutType } from '@kurtosys/types/query/scenarioStatistics/shortcuts/index.js';

import { commonUtils } from '../../../common/index.js';
import { ScenarioStatisticQuery } from '../../queries/ScenarioStatisticQuery.js';

import { ShortcutBase } from './ShortcutBase.js';

export class PortfoliosScatterChartDataShortcut extends ShortcutBase {
	constructor(scenarioStatisticQuery: ScenarioStatisticQuery) {
		super(
			ScenarioStatisticShortcut.portfoliosScatterChartData,
			ShortcutType.directResponse,
			scenarioStatisticQuery,
		);
	}
	getDirectResponse(shortcutOptions: IQueryShortcutOptions, scenarioStatistics: IScenarioStatisticsResponse): any[] {
		const getResponseRow = (
			options: IQueryPortfoliosScatterChartDataOptions = {},
			type: 'portfolio' | 'fund',
			entityResult?: FundResult,
			portfolio?: TPortfolioGetResponse,
			portfolioEntity?: TPortfolioEntityGetResponse,
		): Record<string, any> => {
			const response = {};
			const { portfoliosFields = [], fundsFields = [] } = options;
			const workingExecutionOptions = commonUtils.deepMergeObjects(
				this.scenarioStatisticQuery.executionOptions,
				{},
			) as IExecutionOptions;
			if (portfolio || portfolioEntity) {
				if (!workingExecutionOptions.context) {
					workingExecutionOptions.context = {};
				}
				if (portfolioEntity) {
					workingExecutionOptions.context.portfolioEntity = portfolioEntity;
					const { entity } = portfolioEntity;
					if (entity && entity.type) {
						if (!workingExecutionOptions.context.entityByType) {
							workingExecutionOptions.context.entityByType = {};
						}
						workingExecutionOptions.context.entityByType[entity.type] = entity as any;
					}
				}
				if (portfolio) {
					workingExecutionOptions.context.portfolio = portfolio;
				}
			}
			const fields = type === 'portfolio' ? portfoliosFields : fundsFields;
			fields.forEach((field): void => {
				const { key, entityQuery, statisticQuery, value: explicitValue } = field;
				let value: any = undefined;
				if (!commonUtils.isNullOrUndefined(explicitValue)) {
					value = explicitValue;
				} else if (entityQuery) {
					const query = new this.scenarioStatisticQuery.dependencies.queryClass(entityQuery);
					value = query.execute(workingExecutionOptions);
				} else if (statisticQuery) {
					value = this.scenarioStatisticQuery.getValueForEntity(entityResult, statisticQuery);
				}
				response[key] = value;
			});
			return response;
		};
		const response: any[] = [];
		const options =
			(shortcutOptions && shortcutOptions.options && shortcutOptions.options.portfoliosScatterChartData) || {};
		const { excludeBenchmark = false, includeFunds = false } = options;
		const periodType = this.scenarioStatisticQuery.getSinglePeriodType(shortcutOptions.periods);
		const portfolioId = this.scenarioStatisticQuery.getSinglePortfolioId(shortcutOptions.portfolios);
		if (periodType && portfolioId) {
			const { periods = [] } = scenarioStatistics;
			const period = periods.find((period): boolean => period.periodType === periodType);
			if (period) {
				const { portfolios = [], benchmark } = period;
				const allPortfolios = [...portfolios];
				if (!excludeBenchmark && benchmark) {
					allPortfolios.push(benchmark);
				}
				const portfolioResult = allPortfolios.find((portfolio): boolean => portfolio.id === portfolioId);
				if (portfolioResult) {
					const scenarioPortfolio =
						this.scenarioStatisticQuery.scenario &&
						this.scenarioStatisticQuery.scenario.portfolios &&
						this.scenarioStatisticQuery.scenario.portfolios.find((scenarioPortfolio): boolean =>
							this.scenarioStatisticQuery.idEquals(scenarioPortfolio.portfolioId, portfolioResult.id),
						);
					response.push(
						getResponseRow(
							options,
							'portfolio',
							portfolioResult.portfolio && portfolioResult.portfolio.compositeFund,
							scenarioPortfolio,
						),
					);

					// Exclude the benchmark if we looking at it
					if (!excludeBenchmark && portfolioResult.id !== benchmark.id) {
						const scenarioBenchmark =
							this.scenarioStatisticQuery.scenario &&
							this.scenarioStatisticQuery.scenario.portfolios &&
							this.scenarioStatisticQuery.scenario.portfolios.find((scenarioPortfolio): boolean =>
								this.scenarioStatisticQuery.idEquals(scenarioPortfolio.portfolioId, benchmark.id),
							);
						response.push(
							getResponseRow(
								options,
								'portfolio',
								benchmark.portfolio && benchmark.portfolio.compositeFund,
								scenarioBenchmark,
							),
						);
					}

					if (includeFunds) {
						const { funds = [] } = portfolioResult;
						funds.forEach((fund): void => {
							const portfolioEntity =
								scenarioPortfolio.entities &&
								scenarioPortfolio.entities.find((entity): boolean =>
									this.scenarioStatisticQuery.idEquals(entity.portfolioEntityId, fund.name),
								);
							response.push(getResponseRow(options, 'fund', fund, scenarioPortfolio, portfolioEntity));
						});
					}
				}
			}
		}
		return response;
	}
}
